import safeStringify from 'fast-safe-stringify';
import { snakeCase } from 'lodash-es';
import { LoggerLevel, metrics_category, GetAvgDelta } from './type';
import type { HttpPayload, BrowserCommandClient } from '@slardar/web';
import { BaseError } from '../errors';
import Cache from '../../store/cache';
// import { ENV } from '../../constants/net';

export enum MonitorItem {
  // sdk error
  SdkError = 'sdk.error',
}

export type PerformanceTime = DOMHighResTimeStamp | number;
export type ExtraInfo = Record<string, any>;
export type ExtraInfoProcess = (extra: ExtraInfo) => ExtraInfo;

export interface IMonitor {
  emitHttp: (httpPayload: HttpPayload) => void;
  emitEvent: (event: string, extra?: ExtraInfo) => void;
  emitLog: (level: LoggerLevel, content: string, extra?: ExtraInfo) => void;
  emitError: (error: any, extra?: ExtraInfo) => void;
  // 计数
  emitCounter(key: string, count: number, extra?: ExtraInfo): void;
  // 计时 使用 Performance.now() // Date.now() 度量
  emitDuration(key: string, extra?: ExtraInfo): (extra?: ExtraInfo) => void;
  // 延时
  emitDelay(key: string, delay: number, extra?: ExtraInfo): void;
}

export class Monitor implements IMonitor {
  static level: LoggerLevel = LoggerLevel.info;
  static slardar: BrowserCommandClient;

  private name: string | undefined;
  private extraInfoProcess: ExtraInfoProcess;
  private getAvgDelta: GetAvgDelta = () => 0;

  constructor(
    name?: string,
    extraInfoProcess: ExtraInfoProcess = (extra: ExtraInfo) => extra,
    getAvgDelta?: GetAvgDelta,
  ) {
    this.name = name;
    this.extraInfoProcess = extraInfoProcess;
    typeof getAvgDelta === 'function' && (this.getAvgDelta = getAvgDelta);
  }

  private formatExtraInfo(extra: ExtraInfo = {}, separate = false) {
    const metrics: Record<string, number> = {};
    const categories: Record<string, string> = {
      ocic_sdk_name: 'linkchat-sdk',
      ocic_sdk_version: process.env.DEPLOY_VERSION || 'unknown',
      ocic_user_id: String(Cache.get('userId')) || 'unknown',
      ocic_businessLine_id: `${Cache.get('businessLineId') || 'unknown'}`,
    };

    if (this.name) {
      categories[metrics_category] = this.name;
    }

    for (const key in this.extraInfoProcess(extra)) {
      const snakeKey = snakeCase(key);
      if (typeof extra[key] === 'number') {
        metrics[snakeKey] = extra[key];
      } else {
        categories[snakeKey] =
          typeof extra[key] === 'string'
            ? extra[key]
            : safeStringify(extra[key]);
      }
    }

    if (separate) {
      return { metrics, categories };
    }
    return { extra: { ...metrics, ...categories } };
  }

  performanceNow(): PerformanceTime {
    if (
      typeof performance === 'object' &&
      typeof performance.now === 'function' &&
      typeof performance.timeOrigin === 'number'
    ) {
      return performance.now() + performance.timeOrigin;
    } else {
      return Date.now();
    }
  }

  getLatestResourceEntryByName = (name: string) => {
    return performance?.getEntriesByName?.(name, 'resource')?.pop?.() as
      | PerformanceResourceTiming
      | undefined;
  };

  public emitHttp(httpPayload: HttpPayload) {
    Monitor.slardar?.('report', {
      ev_type: 'http',
      payload: httpPayload,
    });
  }

  public emitEvent(event: string, extra?: ExtraInfo) {
    Monitor.slardar?.('sendEvent', {
      name: this.name ? `${this.name}.${event}` : event,
      ...this.formatExtraInfo(extra, true),
    });
  }

  public emitLog(level: LoggerLevel, content: string, extra?: ExtraInfo) {
    if (level === LoggerLevel.none || level === LoggerLevel.print) return;

    if (Monitor.level <= level) {
      Monitor.slardar?.('sendLog', {
        level: LoggerLevel[level],
        content: content,
        ...this.formatExtraInfo(extra),
      });
    }
  }

  public emitError(error: BaseError, extra?: ExtraInfo) {
    Monitor.slardar?.(
      'captureException',
      error,
      this.formatExtraInfo(extra)?.extra as Record<string, string>,
    );
  }

  public emitCounter(key: string, count: number = 1, extra?: ExtraInfo) {
    if (count === 0) return;

    const suffixKey = key + '.counter';

    this.emitEvent(suffixKey, { ...extra, count });
  }

  public emitDuration(key: string, extra1?: ExtraInfo) {
    const start = this.performanceNow();
    const suffixKey = key + '.duration';
    let duration = 0;

    return (extra2?: ExtraInfo) => {
      if (duration) return;

      duration = this.performanceNow() - start;

      this.emitEvent(suffixKey, { ...extra1, ...extra2, duration });

      return duration;
    };
  }

  public emitLatencyDuration(
    key: string,
    start: number,
    end: number,
    extra?: ExtraInfo,
  ) {
    const suffixKey = key + '.duration';
    const duration = end - start + this.getAvgDelta();

    if (duration > 0) {
      this.emitEvent(suffixKey, {
        ...extra,
        duration,
      });
    }
  }
  public emitDelay(key: string, delay: number, extra?: ExtraInfo) {
    const suffixKey = key + '.delay';

    this.emitEvent(suffixKey, { ...extra, delay });
  }
}

export const setMonitorLevel = (level: LoggerLevel = LoggerLevel.info) => {
  Monitor.level = level;
};

export const setMonitorEngine = (slardar: BrowserCommandClient) => {
  if (slardar) {
    Monitor.slardar = slardar;
  }
};

export default new Monitor();
